import { Document, Image, Page, Text, View } from "@react-pdf/renderer";
import React, { useMemo } from "react";
import ZigLogo from "./assets/logo-zig-azul.png";
import { generateFinancialFileStyle } from "./generate-financial-file-style";
import { RequestClosingOfEventOperationResponseJSON } from "./types";
import { useTranslation } from "react-i18next";
import { EventOperationClosingGroup } from "#resources/api/enterprise-generated";
import { formatDate } from "#resources/helpers/date-format";
import { formatCNPJ, formatCPF } from "#resources/helpers/formats";

interface FormattedFinancialEntry {
	id: string;
	name: string;
	value: number;
	obs: string | null;
	groupId: string;
}

const PERCENTAGE_MULTIPLIER = 100;
const DEFAULT_LENGTH_CNPJ = 14;

const formatDocument = (document: string) => {
	return document.length <= DEFAULT_LENGTH_CNPJ
		? formatCPF(document)
		: formatCNPJ(document);
};

export const GenerateFinancialFile = ({
	data,
}: {
	data: RequestClosingOfEventOperationResponseJSON;
}) => {
	const { t } = useTranslation("place", {
		keyPrefix: "financialFile",
	});

	const getTranslation = (key: string, value: string) => {
		return t(`${key}.${value}` as keyof typeof t);
	};

	// Função para coletar grupos que contêm types (não os types individuais)
	const collectGroupsWithTypes = (
		group: EventOperationClosingGroup,
	): FormattedFinancialEntry[] => {
		const entries: FormattedFinancialEntry[] = [];

		// Se o grupo atual tem types, verifica se algum tem obs
		if (group.types && group.types.length > 0) {
			const hasObs = group.types.some(type => type.obs);

			if (hasObs) {
				// Se algum type tem obs, mostra os types individualmente
				group.types.forEach(type => {
					entries.push({
						id: type.id,
						name: type.name,
						value: type.value / PERCENTAGE_MULTIPLIER,
						obs: type.obs,
						groupId: group.id,
					});
				});
			} else {
				// Se nenhum type tem obs, agrupa pelo grupo
				entries.push({
					id: group.id,
					name: group.name,
					value: group.total / PERCENTAGE_MULTIPLIER, // Usa o total do grupo
					obs: null,
					groupId: group.id,
				});
			}
		}

		// Coleta recursivamente dos subgrupos
		if (group.groups && group.groups.length > 0) {
			group.groups.forEach(subGroup => {
				entries.push(...collectGroupsWithTypes(subGroup));
			});
		}

		return entries;
	};

	// Função para agrupar entradas por subgrupos de um grupo principal
	const groupEntriesByMainGroup = (mainGroupId: string) => {
		const mainGroup = data.zigInvoice.find(group => group.id === mainGroupId);
		if (!mainGroup) return { groups: {}, total: 0 };

		const grouped: Record<
			string,
			{ groupName: string; entries: FormattedFinancialEntry[]; total: number }
		> = {};

		// Se o grupo tem subgrupos, processa cada subgrupo
		if (mainGroup.groups && mainGroup.groups.length > 0) {
			mainGroup.groups.forEach(group => {
				const entries = collectGroupsWithTypes(group);
				if (entries.length > 0 || group.total !== 0) {
					grouped[group.id] = {
						groupName: group.name,
						entries,
						total: group.total / PERCENTAGE_MULTIPLIER, // Converte de centavos para unidade monetária
					};
				}
			});
		}

		// Se o grupo tem types diretos, adiciona como um grupo próprio
		if (mainGroup.types && mainGroup.types.length > 0) {
			const entries = [
				{
					id: mainGroup.id,
					name: mainGroup.name,
					value: mainGroup.total / PERCENTAGE_MULTIPLIER,
					obs: null,
					groupId: mainGroup.id,
				},
			];

			grouped[mainGroup.id] = {
				groupName: mainGroup.name,
				entries,
				total: mainGroup.total / PERCENTAGE_MULTIPLIER, // Converte de centavos para unidade monetária
			};
		}

		return {
			groups: grouped,
			total: mainGroup.total / PERCENTAGE_MULTIPLIER, // Total do grupo principal
		};
	};

	// Agrupa as entradas por grupos principais
	const clientBillingData = useMemo(() => groupEntriesByMainGroup("ClientBilling"), [
		data.zigInvoice,
	]);
	const zigBillingData = useMemo(() => groupEntriesByMainGroup("ZigBilling"), [
		data.zigInvoice,
	]);
	const balanceMovementsData = useMemo(
		() => groupEntriesByMainGroup("BalanceMovements"),
		[data.zigInvoice],
	);

	const formatCurrency = useMemo(
		() => (value: number) => {
			return value.toLocaleString("pt-BR", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
		},
		[],
	);

	const formatDateTime = (date: Date | string | number) => {
		const parsed = new Date(date);

		return parsed.toLocaleString("pt-BR", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false, // formato 24h
		});
	};

	// Interface para dados do grupo
	interface GroupData {
		groups: Record<
			string,
			{
				groupName: string;
				entries: FormattedFinancialEntry[];
				total: number;
			}
		>;
		total: number;
	}

	// Função reutilizável para renderizar uma seção de grupo
	const renderGroupSection = (groupId: string, groupData: GroupData) => {
		const mainGroup = data.zigInvoice.find(group => group.id === groupId);
		if (!mainGroup || Object.keys(groupData.groups).length === 0) return null;

		return (
			<>
				{/* Header do Grupo Principal */}
				<Text style={generateFinancialFileStyle.tableHeader}>
					{mainGroup.name.toUpperCase()}
				</Text>

				{Object.entries(groupData.groups).map(
					([subGroupId, { groupName, entries, total: subGroupTotal }]) => {
						return (
							<View key={subGroupId} style={generateFinancialFileStyle.sectionContainer}>
								<View style={generateFinancialFileStyle.sectionHeader}>
									<View style={generateFinancialFileStyle.sectionIcon} />
									<Text style={generateFinancialFileStyle.sectionTitle}>{groupName}</Text>
								</View>

								<View style={generateFinancialFileStyle.tableContainer}>
									<View style={generateFinancialFileStyle.tableHeaderRow}>
										<Text style={generateFinancialFileStyle.tableCell}>
											{t("titleDescription")}
										</Text>
										<Text style={generateFinancialFileStyle.tableCellRight}>
											{t("value")}
										</Text>
									</View>

									{entries.map((entry, idx) => (
										<View key={idx} style={generateFinancialFileStyle.tableRow}>
											<Text style={generateFinancialFileStyle.tableCell}>
												{entry.obs ? `${entry.name} - ${entry.obs}` : entry.name}
											</Text>
											<Text style={generateFinancialFileStyle.tableCellRight}>
												{formatCurrency(entry.value)}
											</Text>
										</View>
									))}

									<View
										style={[
											generateFinancialFileStyle.tableRow,
											generateFinancialFileStyle.tableRowTotal,
										]}
									>
										<Text style={generateFinancialFileStyle.tableCell}>{t("total")}</Text>
										<Text style={generateFinancialFileStyle.tableCellRight}>
											{formatCurrency(subGroupTotal)}
										</Text>
									</View>
								</View>
							</View>
						);
					},
				)}

				{/* Total do Grupo Principal */}
				<View>
					<View style={generateFinancialFileStyle.sectionHeader}>
						<View style={generateFinancialFileStyle.sectionIcon} />
						<Text
							style={[
								generateFinancialFileStyle.sectionTitle,
								generateFinancialFileStyle.tableRowTotal,
							]}
						>
							{t("totalBalance")} - {mainGroup.name}
						</Text>
					</View>
					<View style={generateFinancialFileStyle.tableContainer}>
						<View style={generateFinancialFileStyle.tableHeaderRow}>
							<Text style={generateFinancialFileStyle.tableCell}>
								{t("titleDescription")}
							</Text>
							<Text style={generateFinancialFileStyle.tableCellRight}>{t("value")}</Text>
						</View>
						<View style={generateFinancialFileStyle.tableRow}>
							<Text
								style={[
									generateFinancialFileStyle.tableCell,
									generateFinancialFileStyle.tableRowTotal,
								]}
							>
								{t("totalBalance")}
							</Text>
							<Text
								style={[
									generateFinancialFileStyle.tableCellRight,
									generateFinancialFileStyle.tableRowTotal,
								]}
							>
								{formatCurrency(groupData.total)}
							</Text>
						</View>
					</View>
				</View>
			</>
		);
	};

	return (
		<Document>
			<Page size="A4" style={generateFinancialFileStyle.page}>
				{/* Header */}
				<View style={generateFinancialFileStyle.header}>
					<Image style={generateFinancialFileStyle.logoImage} src={ZigLogo} />
				</View>

				{/* Title */}
				<Text style={generateFinancialFileStyle.title}>
					{t("financialClosingTitle").toUpperCase()}
				</Text>

				{/* Dados - Zig */}
				<View style={generateFinancialFileStyle.sectionContainer}>
					<View style={generateFinancialFileStyle.sectionHeader}>
						<View style={generateFinancialFileStyle.sectionIcon} />
						<Text style={generateFinancialFileStyle.sectionTitle}>
							{t("zigDataSection")}
						</Text>
					</View>
					<View>
						<View style={generateFinancialFileStyle.sectionInline}>
							<Text style={generateFinancialFileStyle.label}>{t("currency")}:</Text>
							<Text style={generateFinancialFileStyle.value}>{data.currency}</Text>
						</View>

						<View style={generateFinancialFileStyle.sectionInline}>
							<Text style={generateFinancialFileStyle.label}>{t("document")}:</Text>
							<Text style={generateFinancialFileStyle.value}>{data.zigDocument}</Text>

							<Text style={generateFinancialFileStyle.label}>{t("name")}:</Text>
							<Text style={generateFinancialFileStyle.value}>{data.zigName}</Text>
						</View>
						<View style={generateFinancialFileStyle.sectionInline}>
							<Text style={generateFinancialFileStyle.label}>{t("address")}:</Text>
							<Text style={generateFinancialFileStyle.value}>{data.zigAddress}</Text>
						</View>
					</View>
				</View>

				{/* Event ID */}
				<Text style={generateFinancialFileStyle.eventId}>
					{data.platform.toUpperCase() === "N"
						? data.placeName
						: `${data.clientCode} - ${data.placeName}`}
				</Text>

				{/* Dados - Evento */}
				<View style={generateFinancialFileStyle.sectionContainer}>
					<View style={generateFinancialFileStyle.sectionHeader}>
						<View style={generateFinancialFileStyle.sectionIcon} />
						<Text style={generateFinancialFileStyle.sectionTitle}>
							{t("eventDataSection")}
						</Text>
					</View>

					<View style={generateFinancialFileStyle.sectionInline}>
						<Text style={generateFinancialFileStyle.label}>{t("period")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{data?.eventsStartAt && data?.eventsEndAt
								? `${formatDate(new Date(data?.eventsStartAt), {
										custom: "dd/MM/yyyy",
								  })}${" "}	${t("to")} ${formatDate(new Date(data?.eventsEndAt), {
										custom: "dd/MM/yyyy",
								  })}`
								: t("notInformed")}
						</Text>

						<Text style={generateFinancialFileStyle.label}>{t("type")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{getTranslation("eventType", data.eventType)}
						</Text>

						<Text style={generateFinancialFileStyle.label}>{t("zigExecutive")}:</Text>
						<Text style={generateFinancialFileStyle.value}>{data.finderName}</Text>
					</View>

					<View style={generateFinancialFileStyle.sectionInline}>
						<Text style={generateFinancialFileStyle.label}>{t("model")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{getTranslation("eventModel", data.eventModel)}
						</Text>

						<Text style={generateFinancialFileStyle.label}>{t("billing")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{getTranslation("invoiceType", data.eventBillingType)}
						</Text>

						<Text style={generateFinancialFileStyle.label}>
							{t("automaticRepayment")}:
						</Text>
						<Text style={generateFinancialFileStyle.value}>
							{data.automaticRepayment ? t("yes").toUpperCase() : t("no").toUpperCase()}
						</Text>
					</View>

					<View style={generateFinancialFileStyle.sectionInline}>
						<Text style={generateFinancialFileStyle.label}>{t("placeId")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{data.placeId} - {data.placeName}
						</Text>
					</View>

					<View style={generateFinancialFileStyle.sectionInline}>
						<Text style={generateFinancialFileStyle.label}>{t("organizationId")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{data.organizationId} - {data.organizationName}
						</Text>
					</View>

					<View style={generateFinancialFileStyle.sectionInline}>
						<Text style={generateFinancialFileStyle.label}>{t("address")}:</Text>
						<Text style={generateFinancialFileStyle.value}>
							{data.locationOffice} - {data.locationCountry}
						</Text>
					</View>
				</View>

				{/* Dados - Cliente */}
				<View style={generateFinancialFileStyle.sectionContainer}>
					<View style={generateFinancialFileStyle.sectionHeader}>
						<View style={generateFinancialFileStyle.sectionIcon} />
						<Text style={generateFinancialFileStyle.sectionTitle}>
							{t("clientDataSection")}
						</Text>
					</View>

					<View>
						<View style={generateFinancialFileStyle.sectionInline}>
							<Text style={generateFinancialFileStyle.label}>{t("document")}:</Text>
							<Text style={generateFinancialFileStyle.value}>
								{formatDocument(data.clientDocument || "")}
							</Text>
						</View>
					</View>

					<View>
						<View style={generateFinancialFileStyle.sectionInline}>
							<Text style={generateFinancialFileStyle.label}>{t("client")}:</Text>
							<Text style={generateFinancialFileStyle.value}>
								{data.ownerId} - {data.clientCorporateName}
							</Text>
						</View>
					</View>
					<View>
						<View style={generateFinancialFileStyle.sectionInline}>
							<Text style={generateFinancialFileStyle.label}>{t("contract")}:</Text>
							<Text style={generateFinancialFileStyle.value}>
								{data.contractNumber} - {data.platform.toUpperCase()}
							</Text>

							<Text style={generateFinancialFileStyle.label}>
								{t("contractValidity")}:
							</Text>
							<Text style={generateFinancialFileStyle.value}>
								{formatDate(new Date(data.contractStartDate))} {t("to")}{" "}
								{formatDate(new Date(data.contractEndDate))}
							</Text>
						</View>
					</View>
				</View>

				{/* Seção ClientBilling */}
				{renderGroupSection("ClientBilling", clientBillingData)}

				{/* Seção ZigBilling */}
				{renderGroupSection("ZigBilling", zigBillingData)}

				{/* Seção BalanceMovements */}
				{renderGroupSection("BalanceMovements", balanceMovementsData)}
			</Page>

			{/* Second Page */}
			<Page size="A4" style={generateFinancialFileStyle.page}>
				{/* Header */}
				<View style={generateFinancialFileStyle.header}>
					<Image style={generateFinancialFileStyle.logoImage} src={ZigLogo} />
				</View>

				{/* Resultado Final */}
				<View>
					<Text style={generateFinancialFileStyle.tableHeader}>
						{t("finalResultHeader").toUpperCase()}
					</Text>
					<View style={generateFinancialFileStyle.tableContainer}>
						<View style={generateFinancialFileStyle.tableHeaderRow}>
							<Text style={generateFinancialFileStyle.tableCell}>
								{t("titleDescription")}
							</Text>
							<Text style={generateFinancialFileStyle.tableCellRight}>{t("value")}</Text>
						</View>
						<View style={generateFinancialFileStyle.tableRow}>
							<Text style={generateFinancialFileStyle.tableCell}>
								{t("integratedAcquiring")}
							</Text>
							<Text
								style={[
									generateFinancialFileStyle.tableCellRight,
									generateFinancialFileStyle.tableRowTotal,
								]}
							>
								{formatCurrency(
									data.summary.totalIntegratedAcquirer / PERCENTAGE_MULTIPLIER,
								)}
							</Text>
						</View>
						<View style={generateFinancialFileStyle.tableRow}>
							<Text style={generateFinancialFileStyle.tableCell}>
								{t("nonIntegratedAcquiring")}
							</Text>
							<Text
								style={[
									generateFinancialFileStyle.tableCellRight,
									generateFinancialFileStyle.tableRowTotal,
								]}
							>
								{formatCurrency(
									data.summary.totalNonIntegratedAcquirer / PERCENTAGE_MULTIPLIER,
								)}
							</Text>
						</View>

						<View style={generateFinancialFileStyle.tableRow}>
							<Text style={generateFinancialFileStyle.tableCell}>{t("zigBilling")}</Text>
							<Text
								style={[
									generateFinancialFileStyle.tableCellRight,
									generateFinancialFileStyle.tableRowTotal,
								]}
							>
								{formatCurrency(data.summary.totalZigInvoice / PERCENTAGE_MULTIPLIER)}
							</Text>
						</View>

						<View style={generateFinancialFileStyle.tableRow}>
							<Text style={generateFinancialFileStyle.tableCell}>
								{t("balanceMovement")}
							</Text>
							<Text
								style={[
									generateFinancialFileStyle.tableCellRight,
									generateFinancialFileStyle.tableRowTotal,
								]}
							>
								{formatCurrency(data.summary.totalWithdrawals / PERCENTAGE_MULTIPLIER)}
							</Text>
						</View>
					</View>
					<View
						style={[
							generateFinancialFileStyle.tableRow,
							{ borderTopWidth: 2, borderTopColor: "#0066CC", fontWeight: "bold" },
						]}
					>
						<Text style={[generateFinancialFileStyle.tableCell, { fontWeight: "bold" }]}>
							{t("finalBalanceToTransfer")}
						</Text>
						<Text
							style={[
								generateFinancialFileStyle.tableCellRight,
								generateFinancialFileStyle.tableRowTotal,
							]}
						>
							{formatCurrency(data.summary.finalBalance / PERCENTAGE_MULTIPLIER)}
						</Text>
					</View>
				</View>

				{/* Footer Information */}
				<View style={{ position: "absolute", bottom: 30, left: 20, right: 20 }}>
					<View style={{ borderTopWidth: 1, borderTopColor: "#CCCCCC", paddingTop: 15 }}>
						<Text
							style={[
								generateFinancialFileStyle.sectionContent,
								{ textAlign: "center", fontSize: 8, color: "#666666" },
							]}
						>
							{t("issuance")}: {formatDateTime(new Date())} | {t("currency")}:{" "}
							{data.currency || "BRL"} | {t("date")}:{" "}
							{formatDateTime(data.eventOperationBeginAt)} {t("to")}{" "}
							{formatDateTime(data.eventOperationEndAt)} | {t("version")}: 7.00 - pdf.ar |{" "}
							{t("meta")}: M1 {t("type")}: P
						</Text>
					</View>
				</View>
			</Page>

			{data.financialClosingResponse && data.financialClosingResponse.length > 0 && (
				<Page size="A4" style={generateFinancialFileStyle.page}>
					{/* Header */}
					<View style={generateFinancialFileStyle.header}>
						<Image style={generateFinancialFileStyle.logoImage} src={ZigLogo} />
					</View>

					{/* Análise Técnica Header */}
					<Text style={generateFinancialFileStyle.tableHeader}>
						{t("technicalAnalysisHeader").toUpperCase()}
					</Text>

					{/* Análise Técnica Table */}
					<View style={generateFinancialFileStyle.sectionContainer}>
						<View style={generateFinancialFileStyle.tableContainer}>
							<View style={generateFinancialFileStyle.tableHeaderRow}>
								<Text style={generateFinancialFileStyle.tableCell}>{t("title")}</Text>
								<Text style={generateFinancialFileStyle.tableCellCenter}>
									{t("previousValue")}
								</Text>
								<Text style={generateFinancialFileStyle.tableCellRight}>
									{t("newValue")}
								</Text>
							</View>

							{data.financialClosingResponse.map((analysis, idx) => (
								<View key={idx} style={generateFinancialFileStyle.tableRow}>
									<Text style={generateFinancialFileStyle.tableCell}>
										{analysis.title}
									</Text>
									<Text style={generateFinancialFileStyle.tableCellCenter}>
										{formatCurrency(analysis.previousValue / PERCENTAGE_MULTIPLIER)}
									</Text>
									<Text style={generateFinancialFileStyle.tableCellRight}>
										{formatCurrency(analysis.newValue / PERCENTAGE_MULTIPLIER)}
									</Text>
								</View>
							))}
						</View>
					</View>
				</Page>
			)}
		</Document>
	);
};
